import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { PipelineProgram, createSubscription, fetchPrograms } from '../../services/pipelineService';

interface SubscriptionDialogProps {
  open: boolean;
  onClose: (success: boolean) => void;
}

interface FormData {
  programId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientCompany: string;
  startDate: string;
  customSetupFee: string;
  customMonthlyFee: string;
  customLeadsPerWeek: string;
  notes: string;
}

const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    programId: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientCompany: '',
    startDate: new Date().toISOString().split('T')[0],
    customSetupFee: '',
    customMonthlyFee: '',
    customLeadsPerWeek: '',
    notes: '',
  });
  const [programs, setPrograms] = useState<PipelineProgram[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<PipelineProgram | null>(null);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadPrograms();
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (selectedProgram) {
      // Pre-fill fees from selected program
      setFormData((prev) => ({
        ...prev,
        customSetupFee: selectedProgram.setup_fee.toString(),
        customMonthlyFee: selectedProgram.monthly_fee.toString(),
        customLeadsPerWeek: selectedProgram.leads_per_week.toString(),
      }));
    }
  }, [selectedProgram]);

  const loadPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const data = await fetchPrograms();
      // Only show active programs
      const activePrograms = data.filter((p) => p.is_active);
      setPrograms(activePrograms);
    } catch (err) {
      console.error('Error loading programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setLoadingPrograms(false);
    }
  };

  const resetForm = () => {
    setFormData({
      programId: '',
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      clientCompany: '',
      startDate: new Date().toISOString().split('T')[0],
      customSetupFee: '',
      customMonthlyFee: '',
      customLeadsPerWeek: '',
      notes: '',
    });
    setSelectedProgram(null);
    setErrors({});
    setError(null);
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.programId) {
      newErrors.programId = 'Program is required';
    }
    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }
    if (!formData.clientEmail.trim()) {
      newErrors.clientEmail = 'Client email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      newErrors.clientEmail = 'Invalid email format';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (formData.customSetupFee) {
      const setupFee = parseFloat(formData.customSetupFee);
      if (isNaN(setupFee) || setupFee < 0) {
        newErrors.customSetupFee = 'Setup fee must be a positive number';
      }
    }

    if (formData.customMonthlyFee) {
      const monthlyFee = parseFloat(formData.customMonthlyFee);
      if (isNaN(monthlyFee) || monthlyFee < 0) {
        newErrors.customMonthlyFee = 'Monthly fee must be a positive number';
      }
    }

    if (formData.customLeadsPerWeek) {
      const leadsPerWeek = parseInt(formData.customLeadsPerWeek, 10);
      if (isNaN(leadsPerWeek) || leadsPerWeek < 0) {
        newErrors.customLeadsPerWeek = 'Leads per week must be a positive integer';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const subscriptionData = {
        programId: parseInt(formData.programId, 10),
        clientName: formData.clientName.trim(),
        clientEmail: formData.clientEmail.trim(),
        clientPhone: formData.clientPhone.trim() || undefined,
        clientCompany: formData.clientCompany.trim() || undefined,
        startDate: formData.startDate,
        customSetupFee: formData.customSetupFee ? parseFloat(formData.customSetupFee) : undefined,
        customMonthlyFee: formData.customMonthlyFee ? parseFloat(formData.customMonthlyFee) : undefined,
        customLeadsPerWeek: formData.customLeadsPerWeek ? parseInt(formData.customLeadsPerWeek, 10) : undefined,
        notes: formData.notes.trim() || undefined,
      };

      await createSubscription(subscriptionData);
      onClose(true);
    } catch (err: any) {
      console.error('Error creating subscription:', err);
      setError(
        err.response?.data?.message ||
        'Failed to create subscription. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Create New Subscription</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Program Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={programs}
              getOptionLabel={(option) => option.display_name}
              loading={loadingPrograms}
              value={selectedProgram}
              onChange={(_event, newValue) => {
                setSelectedProgram(newValue);
                setFormData((prev) => ({
                  ...prev,
                  programId: newValue ? newValue.id.toString() : '',
                }));
                if (errors.programId) {
                  setErrors((prev) => ({ ...prev, programId: undefined }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Program"
                  required
                  error={!!errors.programId}
                  helperText={errors.programId || 'Select the lead generation program'}
                  disabled={submitting}
                />
              )}
            />
          </Grid>

          {/* Client Information */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Client Name"
              value={formData.clientName}
              onChange={handleChange('clientName')}
              error={!!errors.clientName}
              helperText={errors.clientName}
              disabled={submitting}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client Email"
              type="email"
              value={formData.clientEmail}
              onChange={handleChange('clientEmail')}
              error={!!errors.clientEmail}
              helperText={errors.clientEmail}
              disabled={submitting}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Client Phone"
              value={formData.clientPhone}
              onChange={handleChange('clientPhone')}
              disabled={submitting}
              helperText="Optional"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.clientCompany}
              onChange={handleChange('clientCompany')}
              disabled={submitting}
              helperText="Optional"
            />
          </Grid>

          {/* Subscription Details */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={handleChange('startDate')}
              error={!!errors.startDate}
              helperText={errors.startDate}
              disabled={submitting}
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Custom Pricing (Optional) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Custom Setup Fee (Optional)"
              type="number"
              value={formData.customSetupFee}
              onChange={handleChange('customSetupFee')}
              error={!!errors.customSetupFee}
              helperText={errors.customSetupFee || 'Leave empty to use program default'}
              disabled={submitting}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Custom Monthly Fee (Optional)"
              type="number"
              value={formData.customMonthlyFee}
              onChange={handleChange('customMonthlyFee')}
              error={!!errors.customMonthlyFee}
              helperText={errors.customMonthlyFee || 'Leave empty to use program default'}
              disabled={submitting}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Custom Leads Per Week (Optional)"
              type="number"
              value={formData.customLeadsPerWeek}
              onChange={handleChange('customLeadsPerWeek')}
              error={!!errors.customLeadsPerWeek}
              helperText={errors.customLeadsPerWeek || 'Leave empty to use program default'}
              disabled={submitting}
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={3}
              disabled={submitting}
              helperText="Optional notes about this subscription"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          Create Subscription
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDialog;
